import React, { JSX, useRef } from 'react';
import { FC, MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { find, startCase } from 'lodash';
import classNames from 'classnames';
import {
  Button,
  Icon,
  IconSvg,
  Image,
  Link,
  Select,
  sortVariantByPrice,
  StrainInfo,
} from '@/components';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { GProduct, Variant } from '@/types';
import { NOT_DEFINED } from '@/constants';
import {
  currencyFormat,
  formatSpecialFlowerType,
  formatWeightLabel,
} from '@/utils';
import { useAnalytics, useUiStore } from '@/data';
import {
  useActiveProductBadges,
  useFeaturedFlag,
  useKeyboardCarouselNavigation,
  useWishList,
} from '@/hooks';
import { useProductDetailStore } from '@/kiosk/_state';

import { useProductContext } from './context';

import { ISelectOption } from '../Select/Select';
import { IProductProps } from './Product';
import { CompoundType, UnitPricingTier } from './context/ProductContext';
import { getUnitTierPricing } from './context/utils';
import ProductBadge from './ProductBadge';

import style from './product.module.scss';

const ProductLinkWrapper: FC<ProductLinkWrapperProps> = ({
  className,
  product,
  analyticsListName,
  isInternalWrapper,
  children,
  onKiosk,
}) => {
  const [, setState] = useUiStore();
  const { measureProductClick, measureSelectItemGA4 } = useAnalytics();
  const { setProduct } = useProductDetailStore();

  const trackingClick = () => {
    if (onKiosk) {
      setProduct(product);
    }
    setState({ is_search_open: false });
    measureProductClick(product, 'products-cards');
    measureSelectItemGA4(product, analyticsListName ?? 'product-cards');
  };

  useEffect(() => {
    return () => {
      setProduct(undefined);
    };
  }, []);

  const productName = product?.name || 'product';
  const goToLabel = `Go to ${productName}`;

  return (
    <div
      className={classNames(className, {
        [style.product__link_wrapper]: isInternalWrapper,
      })}
    >
      {onKiosk ? (
        <button
          aria-label={goToLabel}
          className={style.product__stretched_link}
          onClick={trackingClick}
          type="button"
        />
      ) : (
        <Link
          aria-label={goToLabel}
          className={style.product__stretched_link}
          href={product?.url ?? ''}
          onClick={trackingClick}
        >
          {null}
        </Link>
      )}
      {children}
    </div>
  );
};

// Render Image of Product
const ProductImage: FC<IProductProps> = ({ className, product }) => {
  const [load, setLoad] = useState(true);
  const stockPhoto = product?.asset?.stock ?? false;

  const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

  return (
    <div className={classNames(style.product__link_image, className)}>
      <figure
        className={classNames(style.product__image, {
          [style.product__image__load]: load,
        })}
      >
        <Image
          alt={product?.name ?? 'Product image'}
          blurDataURL={
            // eslint-disable-next-line max-len
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1' viewBox='0 0 1 1'%3E%3Crect fill='#ededed' width='1' height='1'/%3E%3C/svg%3E\")"
          }
          fallback={`${REVERSE_PROXY_LINK}/images/product-placeholder.svg`}
          fill
          onLoad={() => {
            setLoad(false);
          }}
          placeholder="blur"
          src={product?.asset?.image as string}
          title={product?.name as string}
        />
        {stockPhoto && (
          <span className={style.product__in_stock}>Stock photo</span>
        )}
      </figure>
    </div>
  );
};

// Render Name of Product (p not heading: product names in listings are not document headings)
const ProductName: FC<{ name: string; className?: string }> = ({
  name,
  className,
}) => {
  return <p className={classNames(style.product__name, className)}>{name}</p>;
};

// Render Active Badge of Product
const ProductBadgeContainer: FC<ProductBadgeContainerProps> = ({
  className,
  product,
  weightVariant,
  onKiosk,
}) => {
  const { badges, shouldRender } = useActiveProductBadges({
    product,
    weightVariant,
    onKiosk,
  });

  const renderedBadges = useMemo(
    () =>
      badges.map(({ props }, index) => {
        const { key: itemKey, ...restProps } = props;
        return (
          <li key={itemKey ?? index}>
            <ProductBadge {...restProps} />
          </li>
        );
      }),
    [badges],
  );

  if (!shouldRender || badges.length === 0) return null;

  return (
    <ul
      aria-label="Product badges"
      className={classNames(style.product__badges, className)}
    >
      {renderedBadges}
    </ul>
  );
};

//Render default product info
export const getDefaultProductInfoComponents = (
  product: GProduct,
  compoundList: CompoundType[],
): JSX.Element[] => {
  return [
    product?.flower_type && (
      <ProductCategory info={product.flower_type} key="category" />
    ),
    compoundList.length > 0 && (
      <ProductCannabinoidProfile
        compoundList={compoundList}
        key="info-product"
      />
    ),
    product?.brand_name && (
      <ProductInfo info={product.brand_name} key="info-brand" type="solid" />
    ),
    product?.subcategory && (
      <ProductInfo
        info={startCase(product.subcategory)}
        key="subcategory"
        type="solid"
      />
    ),
  ].filter(Boolean) as JSX.Element[];
};

//Render Cannabinoid Profile
const ProductCannabinoidProfile: FC<ProductInfoListProps> = ({
  compoundList,
  badgeMode = 'filled',
  badgeClass,
  initDot,
}) => {
  if (!compoundList) return null;

  const isDefault = badgeMode === 'default';

  return (
    <>
      {compoundList.map(({ type, value }) => {
        if (!value) return null;

        const info = (
          <ProductInfo
            className={classNames(style.info, badgeClass)}
            icon={type.toLowerCase()}
            info={isDefault ? `${type}: ${value}` : value}
            key={type}
            type={badgeMode || 'filled'}
          />
        );

        if (badgeMode !== 'default') {
          return info;
        }

        return (
          <div className={style.info__item_conteiner} key={type}>
            {initDot && <div className={style.info__dot} />}
            {info}
          </div>
        );
      })}
    </>
  );
};

// Render Related Info of Product
const ProductInfo: FC<ProductInfoProps> = ({
  className,
  label,
  info,
  showLabel,
  icon,
  type = 'filled',
}) => {
  if (showLabel) {
    return (
      <div className={classNames(style.info__container, className)}>
        {label && <label>{label}</label>}
        <p>{info || NOT_DEFINED}</p>
      </div>
    );
  }

  const ariaLabel = info
    ? label
      ? `${label}: ${info}`
      : `Product ${info}`
    : undefined;

  return (
    <span
      aria-label={ariaLabel}
      className={classNames(style.info, className, {
        [style.info__filled]: type === 'filled',
        [style.info__default]: type === 'default',
        [style.info__solid]: type === 'solid',
      })}
      role={ariaLabel ? 'group' : undefined}
    >
      {icon && type !== 'default' && <IconSvg aria-hidden="true" name={icon} />}{' '}
      {info || NOT_DEFINED}
    </span>
  );
};

// Render Category of Product
const ProductCategory: FC<ProductInfoProps> = ({
  className,
  label,
  info,
  showLabel,
}) => {
  if (!info) {
    return null;
  }

  if (showLabel) {
    return (
      <div className={classNames(style.category__container, className)}>
        {label && <label>{label}</label>}
        <p>{formatSpecialFlowerType(info) || NOT_DEFINED}</p>
      </div>
    );
  }

  return (
    <StrainInfo className={classNames(style.info, className)} strain={info} />
  );
};

// Render THC,CBD,TAC Info of Product
const ProductInfoPack: FC<ProductInfoPackProps> = ({
  className,
  label,
  compoundList,
  showIcon,
}) => {
  if (compoundList?.length === 0) return null;

  const containerAriaLabel = label
    ? `${label}: product compound information`
    : 'Product compound information';

  return (
    <div
      aria-label={containerAriaLabel}
      className={classNames(style.info__container, className)}
      role="group"
    >
      {label && <label>{label}</label>}
      <div className={style.info__body}>
        {compoundList?.map(({ type, value }) => {
          if (!value) return null;

          return (
            <div
              className={classNames(style.info__pack, className, {
                [style.info__pack_badge]: !label,
                [style.info__pack_icon]: !showIcon,
              })}
              key={type}
            >
              {showIcon && (
                <IconSvg aria-hidden="true" name={type.toLowerCase()} />
              )}
              <span>{type}</span>
              <span>{value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Render Price of Product
const ProductPrice: FC<ProductPriceProps> = ({
  className,
  weightVariant,
  elementVariants,
  isOverflow = false,
  isSpecial,
  type,
}) => {
  const { unitPricingTier, product } = useProductContext();
  const { isTaxAppliedMessage, showStriketroughPrice } = useFeaturedFlag();
  const siblingsSize = product.siblings?.length ?? 0;
  const overflow = isOverflow && siblingsSize <= 1;

  const unitTierPricing = useMemo(() => {
    if (!unitPricingTier) return null;

    return getUnitTierPricing(unitPricingTier, product.pricing);
  }, [unitPricingTier, product.pricing]);

  const labelSalePrice = `$${currencyFormat(
    unitTierPricing?.saleCents ?? weightVariant?.salePrice ?? 0,
  )}`;
  const labelPrice = `$${currencyFormat(
    unitTierPricing?.originalCents ?? weightVariant?.price ?? 0,
  )}`;
  const discount = `${weightVariant?.discount_percent}%`;

  // Build comprehensive aria-label
  const hasPreviousPrice = isSpecial && showStriketroughPrice;
  const hasDiscount = !!weightVariant?.discount_percent;

  let ariaLabel = `Product price: ${labelSalePrice}`;
  if (hasPreviousPrice) {
    ariaLabel += `, previous price: ${labelPrice}`;
  }
  if (hasDiscount) {
    ariaLabel += `, discount: ${discount}`;
  }
  if (isTaxAppliedMessage) {
    ariaLabel += ', tax included';
  }

  const priceMoreVariation = (
    <div
      className={classNames(style.price__ins_container, {
        [style.price__ins_container_overflow]: overflow,
      })}
    >
      <ins aria-hidden="true">{labelSalePrice}</ins>
      {elementVariants}
    </div>
  );

  const strikeTroughPrice =
    isSpecial && showStriketroughPrice ? (
      <del aria-hidden="true">{labelPrice}</del>
    ) : null;

  const taxMessage = isTaxAppliedMessage ? (
    <span aria-hidden="true" className={style.price__tax_message}>
      TAX INCL.
    </span>
  ) : null;

  const discountMessage = weightVariant?.discount_percent ? (
    <span aria-hidden="true" className={style.price__discount}>
      {`-${discount}`}
    </span>
  ) : null;

  if (type === 'row') {
    return (
      <div
        aria-label={ariaLabel}
        className={classNames(style.price, style.price__row_type, className)}
        role="group"
      >
        {priceMoreVariation}
        {strikeTroughPrice}
        {isTaxAppliedMessage && taxMessage}
        {discountMessage}
      </div>
    );
  }

  if (type === 'expanded') {
    return (
      <div
        aria-label={ariaLabel}
        className={classNames(
          style.price,
          style.price__expanded_type,
          { [style.price__overflow]: overflow },
          className,
        )}
        role="group"
      >
        <div
          className={classNames(style.price__column, {
            [style.price__column_overflow]: overflow,
          })}
        >
          {priceMoreVariation}
          {isTaxAppliedMessage && taxMessage}
        </div>
        {strikeTroughPrice || discountMessage ? (
          <div aria-hidden="true" className={style.price__column_right}>
            {strikeTroughPrice}
            {discountMessage}
          </div>
        ) : null}
      </div>
    );
  }

  if (type === 'expanded-tax') {
    return (
      <div
        aria-label={ariaLabel}
        className={classNames(
          style.price,
          style.price__expanded_tax_type,
          {
            [style.price__overflow]: overflow,
          },
          className,
        )}
        role="group"
      >
        <div
          className={classNames(style.price__column, {
            [style.price__column_overflow]: overflow,
          })}
        >
          {strikeTroughPrice}
          {priceMoreVariation}
        </div>
        {isTaxAppliedMessage && (
          <div aria-hidden="true" className={style.price__column_right}>
            {taxMessage}
          </div>
        )}
      </div>
    );
  }

  if (type === 'column-container') {
    return (
      <div
        aria-label={ariaLabel}
        className={classNames(style.price, style.price__column, className)}
        role="group"
      >
        {priceMoreVariation}
        {(strikeTroughPrice || isTaxAppliedMessage) && (
          <div aria-hidden="true" className={style.price__secundary_info}>
            {strikeTroughPrice}
            {strikeTroughPrice && taxMessage && (
              <div aria-hidden="true" className={style.dot} />
            )}
            {isTaxAppliedMessage && taxMessage}
          </div>
        )}
      </div>
    );
  }

  if (type === 'inverted') {
    return (
      <div
        aria-label={ariaLabel}
        className={classNames(
          style.price,
          style.price__inverted_type,
          className,
        )}
        role="group"
      >
        {priceMoreVariation}
        {(strikeTroughPrice || isTaxAppliedMessage) && (
          <div aria-hidden="true" className={style.price__secundary_info}>
            {strikeTroughPrice}
            {isTaxAppliedMessage && taxMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      aria-label={ariaLabel}
      className={classNames(style.price, className)}
      role="group"
    >
      {strikeTroughPrice}
      <ins aria-hidden="true">{labelSalePrice}</ins>
    </div>
  );
};

// Render Weight Variants of Product
const ProductVariants: FC<ProductVariantsProps> = props => {
  const { product } = useProductContext();
  const siblingsSize = product.siblings?.length ?? 0;

  if (siblingsSize > 1) {
    return <ProductSiblings {...props} />;
  }

  if (product.pricing?.tier_method === 'UNIT') {
    return <ProductUnitPricingTiers {...props} />;
  }

  return <ProductWeightVariants {...props} />;
};

// Render Sibling Products (by custom size) as a select
const ProductSiblings: FC<ProductVariantsProps> = ({
  className,
  label,
  type = 'default',
  showRemainingCount = true,
}) => {
  const { product, selectedProductId, setSelectedProductId } =
    useProductContext();
  const handleKeyDown = useKeyboardCarouselNavigation<HTMLDivElement>();
  const buttonRefs = useRef<Array<HTMLDivElement | null>>([]);

  const siblings = product.siblings ?? [];

  const siblingOptions: ISelectOption<string>[] = siblings
    .filter(sibling => Boolean(sibling?.id && (sibling?.size || sibling?.name)))
    .map(sibling => {
      const variant = sibling?.variants?.[0];
      const size = sibling?.size;

      const weight = formatWeightLabel(variant?.type!);

      const label = weight?.endsWith('G')
        ? `${weight.split('G')[0]} gram`
        : weight || size || '';

      return {
        label,
        value: sibling?.id as string,
      };
    });

  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(0, siblingOptions.length);
  }, [siblingOptions]);

  if (!siblingOptions.length) return null;

  const selectedOption =
    siblingOptions.find(option => option.value === selectedProductId) ??
    siblingOptions[0];

  const handleSiblingChange = (option: ISelectOption<string>) => {
    setSelectedProductId(option.value);
  };

  if (type === 'carousel') {
    const carouselAriaLabel = 'Product size selector';
    return (
      <div
        aria-label={carouselAriaLabel}
        className={style.variant_carousel__container}
        role="group"
      >
        {label && <label>{label}</label>}
        <ProductInfoCarousel
          aria-label={`Select product size. Current selection: ${selectedOption.label}`}
          disableAccess={false}
          role="radiogroup"
        >
          {siblingOptions.map((option, index) => (
            <div
              aria-checked={selectedOption.value === option.value}
              aria-label={`Select product size ${option.label}`}
              className={classNames(style.variants__item, {
                [style.variants__item_active]:
                  selectedOption.value === option.value,
              })}
              key={option.value}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleSiblingChange(option);
              }}
              onKeyDown={e =>
                handleKeyDown(e, index, buttonRefs.current, () =>
                  handleSiblingChange(siblingOptions[index]),
                )
              }
              ref={el => {
                buttonRefs.current[index] = el;
              }}
              role="radio"
              tabIndex={selectedOption.value === option.value ? 0 : -1}
            >
              {option.label}
            </div>
          ))}
        </ProductInfoCarousel>
      </div>
    );
  }

  const containerAriaLabel = 'Product size selector';

  const selectAriaLabel = selectedOption?.label
    ? `Select product size. Current selection: ${selectedOption.label}${
        siblingOptions.length > 1
          ? `. ${siblingOptions.length - 1} more options available`
          : ''
      }`
    : 'Select product size';

  return (
    <div
      aria-label={containerAriaLabel}
      className={classNames(style.variant_select_container, className)}
      role="group"
    >
      <span aria-hidden="true">/&nbsp;</span>
      <Select
        ariaLabel={selectAriaLabel}
        className={style.variant_select}
        defaultValue={selectedOption}
        menuClassName={style.variant_select__menu}
        name="product_sibling"
        onChange={handleSiblingChange}
        options={siblingOptions}
        placement="top-start"
        showRemainingCount={showRemainingCount}
        value={selectedOption}
      />
    </div>
  );
};

// Render Weight Variants of Product
const ProductWeightVariants: FC<ProductVariantsProps> = ({
  className,
  label,
  type = 'default',
  showRemainingCount = true,
}) => {
  const { weightVariant, variants, updateWeightVariant } = useProductContext();
  const handleKeyDown = useKeyboardCarouselNavigation<HTMLDivElement>();
  const buttonRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [variantsSelectOptions, setVariantsSelectOptions] = useState<
    ISelectOption<string>[]
  >([]);

  const [selectedVariantOption, setSelectedVariantOption] = useState<
    ISelectOption<string>
  >({
    label: formatWeightLabel(weightVariant?.type as string),
    value: weightVariant?.id!,
  });

  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(
      0,
      variantsSelectOptions.length,
    );
  }, [variantsSelectOptions]);

  useEffect(() => {
    if (!variants) return;

    const sorted = sortVariantByPrice(variants as Variant[]).map(v => ({
      label: formatWeightLabel(v?.type as string),
      value: v.id as string,
    }));

    setVariantsSelectOptions(sorted);
  }, [variants]);

  const handleVariantChange = (option: ISelectOption<string>) => {
    setSelectedVariantOption(option);
    updateWeightVariant(option);
  };

  const filteredVariantsSelectOptions = variantsSelectOptions.filter(
    ({ label }) => Boolean(label),
  );

  if (!filteredVariantsSelectOptions.length) return null;

  const hasMultipleVariants = filteredVariantsSelectOptions.length > 1;

  if (type === 'carousel') {
    const carouselAriaLabel = 'Weight variants selector';
    return (
      <div
        aria-label={carouselAriaLabel}
        className={style.variant_carousel__container}
        role="group"
      >
        {label && <label>{label}</label>}
        <ProductInfoCarousel
          aria-label={`Select weight variant. Current selection: ${selectedVariantOption.label}`}
          disableAccess={false}
          role="radiogroup"
        >
          {filteredVariantsSelectOptions.map((wv, index) => (
            <div
              aria-checked={selectedVariantOption.value === wv.value}
              aria-label={`Select weight variant ${wv.label}`}
              className={classNames(style.variants__item, {
                [style.variants__item_active]:
                  selectedVariantOption.value === wv.value,
              })}
              key={wv.value}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleVariantChange(wv);
              }}
              onKeyDown={e =>
                handleKeyDown(e, index, buttonRefs.current, () =>
                  handleVariantChange(filteredVariantsSelectOptions[index]),
                )
              }
              ref={el => {
                buttonRefs.current[index] = el;
              }}
              role="radio"
              tabIndex={selectedVariantOption.value === wv.value ? 0 : -1}
            >
              {wv.label}
            </div>
          ))}
        </ProductInfoCarousel>
      </div>
    );
  }

  const variantLabel =
    weightVariant?.type && weightVariant.type !== '0_unit'
      ? formatWeightLabel(weightVariant.type)
      : '';

  const containerAriaLabel = hasMultipleVariants
    ? 'Product weight variant selector'
    : variantLabel
    ? `Product weight variant: ${variantLabel}`
    : 'Product weight variant';

  const selectAriaLabel = selectedVariantOption?.label
    ? `Select weight variant. Current selection: ${
        selectedVariantOption.label
      }${
        hasMultipleVariants
          ? `. ${
              filteredVariantsSelectOptions.length - 1
            } more options available`
          : ''
      }`
    : 'Select weight variant';

  return (
    <div
      aria-label={containerAriaLabel}
      className={classNames(style.variant_select_container, className, {
        [style.variant_select_container_overflow]: !hasMultipleVariants,
      })}
      role="group"
    >
      {type === 'default' && !!weightVariant?.type && (
        <span aria-hidden="true">/&nbsp;</span>
      )}
      {hasMultipleVariants ? (
        <Select
          ariaLabel={selectAriaLabel}
          className={style.variant_select}
          defaultValue={selectedVariantOption}
          menuClassName={style.variant_select__menu}
          name="weight_variant"
          onChange={handleVariantChange}
          options={filteredVariantsSelectOptions}
          placement="top-start"
          showRemainingCount={showRemainingCount}
          value={selectedVariantOption}
        />
      ) : (
        <span aria-hidden="true" className={style.variation__message}>
          {variantLabel}
        </span>
      )}
    </div>
  );
};

// Render Unit Pricing Tiers of Product
const ProductUnitPricingTiers: FC<ProductVariantsProps> = ({
  className,
  label,
  type = 'default',
  showRemainingCount = true,
}) => {
  const buttonRefs = useRef<Array<HTMLDivElement | null>>([]);
  const { product, unitPricingTiers, unitPricingTier, updateUnitPricingTier } =
    useProductContext();
  const handleKeyDown = useKeyboardCarouselNavigation<HTMLDivElement>();

  const unitPricingTiersOptions = useMemo(
    () =>
      unitPricingTiers?.map(({ qty, price }) => {
        const totalPrice = price * qty;
        return {
          label: `${qty} for $${currencyFormat(totalPrice * 100)}`,
          value: { qty, price },
        };
      }) ?? [],
    [product],
  );

  const selectedTier = useMemo(() => {
    return find(
      unitPricingTiersOptions,
      option =>
        option.value.qty === unitPricingTier?.qty &&
        option.value.price === unitPricingTier?.price,
    );
  }, [unitPricingTiersOptions, unitPricingTier]);

  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(
      0,
      unitPricingTiersOptions.length,
    );
  }, [unitPricingTiersOptions]);

  const hasMultipleTiers = unitPricingTiersOptions?.length > 1;

  const handleSelectedVariation = (
    event: MouseEvent<Element, globalThis.MouseEvent>,
    option: UnitPricingTier,
  ) => {
    event?.stopPropagation();
    event?.preventDefault();

    updateUnitPricingTier(option);
  };

  const handleTierChange = (option: UnitPricingTier) => {
    updateUnitPricingTier(option);
  };

  if (type === 'carousel') {
    const carouselAriaLabel = 'Unit pricing tiers selector';
    const currentSelection = selectedTier?.label || 'None';
    return (
      <div
        aria-label={carouselAriaLabel}
        className={style.variant_carousel__container}
        role="group"
      >
        {label && <label>{label}</label>}
        <ProductInfoCarousel
          aria-label={`Select unit pricing tier. Current selection: ${currentSelection}`}
          disableAccess={false}
          role="radiogroup"
        >
          {unitPricingTiersOptions?.map((wv, index) => {
            const isSelected =
              wv.value.qty === unitPricingTier?.qty &&
              wv.value.price === unitPricingTier?.price;

            return (
              <div
                aria-checked={isSelected}
                aria-label={`Select unit pricing tier ${wv.label}`}
                className={classNames(style.variants__item, {
                  [style.variants__item_active]:
                    wv.value.qty === selectedTier?.value.qty &&
                    wv.value.price === selectedTier?.value.price,
                })}
                key={`${wv.value?.price}-${wv.value?.qty}`}
                onClick={e => handleSelectedVariation(e, wv.value)}
                onKeyDown={e =>
                  handleKeyDown(e, index, buttonRefs.current, () =>
                    updateUnitPricingTier(unitPricingTiersOptions[index].value),
                  )
                }
                ref={el => {
                  buttonRefs.current[index] = el;
                }}
                role="radio"
                tabIndex={isSelected ? 0 : -1}
              >
                {wv.label}
              </div>
            );
          })}
        </ProductInfoCarousel>
      </div>
    );
  }

  const tierLabel = selectedTier?.label || '';
  const containerAriaLabel = hasMultipleTiers
    ? 'Product unit pricing tier selector'
    : tierLabel
    ? `Product unit pricing tier: ${tierLabel}`
    : 'Product unit pricing tier';

  const selectAriaLabel = selectedTier?.label
    ? `Select unit pricing tier. Current selection: ${selectedTier.label}`
    : 'Select unit pricing tier';

  return (
    <div
      aria-label={containerAriaLabel}
      className={classNames(style.variant_select_container, className, {
        [style.variant_select_container_overflow]: !hasMultipleTiers,
      })}
      role="group"
    >
      {type === 'default' && <span aria-hidden="true">/&nbsp;</span>}
      {hasMultipleTiers ? (
        <Select
          ariaLabel={selectAriaLabel}
          className={style.variant_select}
          defaultValue={selectedTier}
          menuClassName={style.variant_select__menu}
          name="weight_variant"
          onChange={({ value }) => handleTierChange(value)}
          options={unitPricingTiersOptions}
          placement="top-start"
          showRemainingCount={showRemainingCount}
          value={selectedTier}
        />
      ) : (
        <span aria-hidden="true" className={style.variation__message}>
          {tierLabel}
        </span>
      )}
    </div>
  );
};

// Render Carousel With Info Product
const ProductInfoCarousel: FC<{
  className?: string;
  classWrapper?: string;
  direction?: 'horizontal' | 'vertical';
  children: ReactNode[];
  ariaLabel?: string;
  role?: string;
  disableAccess?: boolean;
}> = ({
  className,
  classWrapper,
  children,
  direction = 'horizontal',
  ariaLabel,
  role,
  disableAccess = true,
}) => {
  const isVertidcalDir = direction === 'vertical';

  const cancelEvent = e => {
    e.preventDefault();
    e.stopPropagation();
    return;
  };

  return (
    <div
      className={classNames(style.product__info_carousel_wrapper, classWrapper)}
    >
      <Swiper
        aria-label={ariaLabel}
        className={classNames(style.product__info_carousel, className, {
          [style.product__info_carousel_vertical]: isVertidcalDir,
        })}
        direction={direction}
        freeMode
        grabCursor
        mousewheel
        role={role ?? (ariaLabel ? 'group' : undefined)}
        simulateTouch
        slidesPerView={'auto'}
        spaceBetween={4}
      >
        {children.map((child, index) => (
          <SwiperSlide
            className={classNames({
              [style.product__swipper_slide]: isVertidcalDir,
            })}
            key={index}
            onClick={e => {
              if (disableAccess) {
                cancelEvent(e);
              }
            }}
            role={disableAccess ? 'presentation' : undefined}
            style={{
              width: 'auto',
            }}
            tabIndex={disableAccess ? 0 : undefined}
          >
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

// Render Reviews and Rating of Product
const ProductReview: FC<ProductReviewProps> = ({
  className,
  label,
  rating,
  reviews,
}) => {
  return (
    <div className={classNames(style.review__container, className)}>
      {label && <label>{label}</label>}
      <div className={style.review__info}>
        <Icon name={rating > 0 ? 'icon_star' : 'start'} />
        <span>{rating}</span>
        <p>{` (${reviews} reviews)`}</p>
      </div>
    </div>
  );
};

// Render Favorite Action of Product
const ProductActionFavorite: FC<IProductProps> = ({ className, product }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const { wishlistActive } = useFeaturedFlag();
  const { measureAddToWishlistGA4 } = useAnalytics();
  const { favProductsIds, addFav, removeFav } = useWishList();

  useEffect(() => {
    const p = favProductsIds.find(p => p == product.id);
    if (p !== undefined) {
      setIsFavorite(true);
      return;
    }
    setIsFavorite(false);
  }, [favProductsIds]);

  const handleFavorite = (product: GProduct) => {
    if (isFavorite) {
      removeFav(product.id as string);
      return;
    }
    measureAddToWishlistGA4(product);
    addFav(product.id as string);
  };

  if (!wishlistActive) {
    return null;
  }

  const productName = product?.name || 'this product';

  return (
    <Button
      ariaLabel={
        isFavorite
          ? `Remove ${productName} from favorites`
          : `Add ${productName} to favorites`
      }
      className={classNames(style.favorite_action, className)}
      color="link"
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        handleFavorite(product);
      }}
      size="sm"
    >
      <Icon
        aria-hidden="true"
        name={isFavorite ? 'favorite-solid' : 'favorite'}
      />
    </Button>
  );
};

// Render The Counter Of Added Products
const ProductIconShopping: FC<{ className?: string; quantity: number }> = ({
  className,
  quantity,
}) => {
  const ariaLabel = quantity
    ? `Products in cart: ${quantity} ${quantity === 1 ? 'item' : 'items'}`
    : 'Shopping cart icon';

  return (
    <div
      aria-label={ariaLabel}
      className={classNames(style.icon__shopping, className)}
      role="img"
    >
      <IconSvg aria-hidden="true" name="shopping" />
      {quantity ? (
        <span aria-hidden="true" className={style.qty__badge}>
          {quantity}
        </span>
      ) : null}
    </div>
  );
};

interface ProductLinkWrapperProps {
  className?: string;
  product: GProduct;
  analyticsListName?: string;
  isInternalWrapper?: boolean;
  children: ReactNode;
  onKiosk?: boolean;
}

interface ProductInfoProps {
  className?: string;
  label?: string;
  info?: string | null;
  icon?: string;
  showLabel?: boolean;
  type?: 'default' | 'filled' | 'solid';
}

interface ProductInfoPackProps {
  className?: string;
  compoundList: CompoundType[];
  label?: string;
  showIcon?: boolean;
}
interface ProductPriceProps {
  className?: string;
  weightVariant: Variant;
  elementVariants?: ReactNode;
  isSpecial?: boolean;
  isOverflow?: boolean;
  type?:
    | 'default'
    | 'expanded'
    | 'row'
    | 'inverted'
    | 'column-container'
    | 'expanded-tax';
}
interface ProductVariantsProps {
  className?: string;
  label?: string;
  type?: 'default' | 'carousel';
  showRemainingCount?: boolean;
}
interface ProductReviewProps {
  className?: string;
  label?: string;
  rating: number;
  reviews: number;
}
interface ProductBadgeContainerProps {
  product: GProduct;
  weightVariant: Variant;
  className?: string;
  onKiosk?: boolean;
}

interface ProductInfoListProps {
  compoundList: CompoundType[];
  badgeMode?: 'filled' | 'default' | 'solid';
  badgeClass?: string;
  initDot?: boolean;
}

export {
  ProductLinkWrapper,
  ProductBadgeContainer,
  ProductCannabinoidProfile,
  ProductImage,
  ProductName,
  ProductCategory,
  ProductPrice,
  ProductReview,
  ProductActionFavorite,
  ProductVariants,
  ProductInfoPack,
  ProductInfo,
  ProductIconShopping,
  ProductInfoCarousel,
  ProductUnitPricingTiers,
};
